import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, FileText, Folder, Hash, Pin, CornerDownLeft, Loader2, Users2 } from 'lucide-react';

/**
 * Notes Command Palette — a separate overlay (Cmd/Ctrl+K or "/") that searches
 * across notes (full-text), folders, tags, and the "Pinned Notes" quick view.
 * Keyboard: ↑/↓ to move, Enter to open, Esc to close. Non-recursive.
 */
export function CommandPalette({
  open,
  onClose,
  folders = [],
  sharedFolders = [],
  tags = [],
  searchFn,
  onOpenNote,
  onSelectFolder,
  onSelectSharedFolder,
  onSelectTag,
  onGoView,
}) {
  const [q, setQ] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setNotes([]);
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Debounced full-text note search (server-side), with in-flight cancellation.
  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (!term) { setNotes([]); setLoading(false); return undefined; }
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try { const data = await searchFn(term); if (!cancel) setNotes(data || []); }
      catch { if (!cancel) setNotes([]); }
      finally { if (!cancel) setLoading(false); }
    }, 180);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, open, searchFn]);

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    const out = [];
    // Jump-to (quick views)
    [
      { id: 'q-pinned', type: 'quick', label: 'Pinned Notes', icon: Pin, run: () => onGoView('pinned') },
      { id: 'q-all', type: 'quick', label: 'All Notes', icon: FileText, run: () => onGoView('all') },
    ].filter((x) => !term || x.label.toLowerCase().includes(term)).forEach((x) => out.push(x));
    // Notes (server full-text)
    notes.forEach((n) => out.push({
      id: `n-${n.id}`, type: 'note', label: n.title || 'Untitled', sub: n.folder_name,
      snippet: n.snippet, shared_by: n.shared_by, icon: FileText, run: () => onOpenNote(n.id),
    }));
    // Own folders
    (folders || []).filter((f) => !term || (f.name || '').toLowerCase().includes(term)).slice(0, 6)
      .forEach((f) => out.push({ id: `f-${f.id}`, type: 'folder', label: f.name, icon: Folder, run: () => onSelectFolder(f.id) }));
    // Shared folders
    (sharedFolders || []).filter((f) => !term || (f.name || '').toLowerCase().includes(term)).slice(0, 4)
      .forEach((f) => out.push({ id: `sf-${f.id}`, type: 'folder', label: f.name, sub: 'Shared', shared: true, icon: Folder, run: () => onSelectSharedFolder(f.id) }));
    // Tags
    (tags || []).filter((t) => !term || (t.tag || '').toLowerCase().includes(term)).slice(0, 8)
      .forEach((t) => out.push({ id: `t-${t.tag}`, type: 'tag', label: t.tag, icon: Hash, run: () => onSelectTag(t.tag) }));
    return out;
  }, [q, notes, folders, sharedFolders, tags, onGoView, onOpenNote, onSelectFolder, onSelectSharedFolder, onSelectTag]);

  useEffect(() => { setActive(0); }, [items.length]);

  const run = useCallback((it) => { if (!it) return; it.run(); onClose(); }, [onClose]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(items.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(items[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  }, [items, active, run, onClose]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const SECTION_LABEL = { quick: 'Jump to', note: 'Notes', folder: 'Folders', tag: 'Tags' };
  let lastType = null;

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 pt-[12vh]" data-testid="notes-command-palette" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" aria-hidden="true" />
      <div role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-[640px] overflow-hidden rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]">
        <div className="flex items-center gap-2 border-b border-[var(--n-line)] px-3.5">
          <Search size={16} className="shrink-0 text-[var(--n-muted-2)]" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            data-testid="notes-command-input" placeholder="Search notes, folders, tags…"
            className="w-full bg-transparent py-3.5 text-sm text-[var(--n-ink)] outline-none placeholder:text-[var(--n-muted-2)]" />
          {loading && <Loader2 size={15} className="shrink-0 animate-spin text-[var(--n-muted-2)]" data-testid="notes-command-loading" />}
          <kbd className="hidden shrink-0 rounded border border-[var(--n-border)] px-1.5 py-0.5 text-[10px] text-[var(--n-muted-2)] sm:inline">Esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[52vh] overflow-y-auto n-scroll p-1.5">
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-[13px] text-[var(--n-muted)]" data-testid="notes-command-empty">
              {loading ? 'Searching…' : (q.trim() ? `No matches for “${q.trim()}”.` : 'Type to search notes, folders and tags.')}
            </div>
          ) : items.map((it, idx) => {
            const header = it.type !== lastType ? SECTION_LABEL[it.type] : null;
            lastType = it.type;
            const Icon = it.icon;
            return (
              <React.Fragment key={it.id}>
                {header && <div className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">{header}</div>}
                <button data-idx={idx} data-testid={`notes-command-item-${it.type}`}
                  onMouseEnter={() => setActive(idx)} onClick={() => run(it)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${active === idx ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                  <Icon size={15} className={`shrink-0 ${it.type === 'tag' ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)]'}`} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-[var(--n-ink)]">{it.type === 'tag' ? `#${it.label}` : it.label}</span>
                    {it.snippet && <span className="truncate text-[11.5px] text-[var(--n-muted)]">{it.snippet}</span>}
                  </span>
                  {it.shared_by && <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-[var(--n-accent)]"><Users2 size={10} /> {it.shared_by}</span>}
                  {it.sub && <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-[var(--n-muted-2)]">{it.shared ? <Users2 size={10} /> : <Folder size={10} />} {it.sub}</span>}
                  {active === idx && <CornerDownLeft size={13} className="shrink-0 text-[var(--n-muted-2)]" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
